import type { DisplayUnits, ObjectSize } from './types'
import { formatDistanceM, metersFromFeet, metersFromMiles } from './format'
import { BRWN_PLUME_TYPE, brwnPlumeZoneCfg } from './eggs/brwnPlume'

export const TYPE_COLORS = ['#ffffff', '#FAC775', '#E24B4A', '#7FE0C0', '#82C4F8', '#2C2C2A'] as const
export const DRAW_COLORS = ['#E24B4A', '#185FA5', '#0F6E56', '#D85A30', '#534AB7', '#FAC775', '#ffffff', '#2C2C2A'] as const

export const TYPE_SIZE_PX: Record<ObjectSize, number> = { sm: 13, md: 17, lg: 23, xl: 31 }
export const TYPE_SIZE_LABEL: Record<ObjectSize, string> = {
  sm: 'Small',
  md: 'Medium',
  lg: 'Large',
  xl: 'XL',
}

export type ZoneCfg = {
  min: number
  max: number
  step: number
  def: number
  presets: number[]
  label: string
  /** Number field and unit. Hurricane wind fields are miles, not feet. */
  widthInMiles?: boolean
}

/** Imperial-first zone sizes (round feet → meters). Metric UI still converts cleanly enough. */
export const ZONE_CFG: Record<string, ZoneCfg> = {
  fire: {
    min: metersFromFeet(50),
    max: metersFromFeet(1300),
    step: metersFromFeet(25),
    def: metersFromFeet(175),
    presets: [metersFromFeet(125), metersFromFeet(300), metersFromFeet(525)],
    label: 'Isolation Distance',
  },
  explosion: {
    min: metersFromFeet(50),
    max: metersFromFeet(1300),
    step: metersFromFeet(25),
    def: metersFromFeet(250),
    presets: [metersFromFeet(175), metersFromFeet(325), metersFromFeet(650)],
    label: 'Blast radius',
  },
  vapor_cloud: {
    min: metersFromFeet(50),
    max: metersFromFeet(3300),
    step: metersFromFeet(25),
    def: metersFromFeet(500),
    presets: [metersFromFeet(150), metersFromFeet(300), metersFromFeet(650), metersFromFeet(1000)],
    label: 'Vapor footprint',
  },
  gas_leak: {
    min: metersFromFeet(50),
    max: metersFromFeet(3300),
    step: metersFromFeet(25),
    def: metersFromFeet(500),
    presets: [metersFromFeet(150), metersFromFeet(300), metersFromFeet(650), metersFromFeet(1000)],
    label: 'Leak footprint',
  },
  [BRWN_PLUME_TYPE]: brwnPlumeZoneCfg(),
  leak: {
    min: metersFromFeet(50),
    max: metersFromFeet(3300),
    step: metersFromFeet(25),
    def: metersFromFeet(500),
    presets: [metersFromFeet(150), metersFromFeet(300), metersFromFeet(650), metersFromFeet(1000)],
    label: 'Leak footprint',
  },
  hot_zone: {
    min: metersFromFeet(50),
    max: metersFromFeet(1000),
    step: metersFromFeet(25),
    def: metersFromFeet(250),
    presets: [metersFromFeet(150), metersFromFeet(300), metersFromFeet(500), metersFromFeet(650)],
    label: 'Hot zone radius',
  },
  isolation: {
    min: metersFromFeet(50),
    max: metersFromFeet(1600),
    step: metersFromFeet(25),
    def: metersFromFeet(500),
    presets: [metersFromFeet(250), metersFromFeet(500), metersFromFeet(800), metersFromFeet(1300)],
    label: 'Isolation radius',
  },
  evacuate: {
    min: metersFromFeet(150),
    max: metersFromFeet(3300),
    step: metersFromFeet(25),
    def: metersFromFeet(1000),
    presets: [metersFromFeet(500), metersFromFeet(1000), metersFromFeet(1600), metersFromFeet(2600)],
    label: 'Evacuation radius',
  },
  shelter: {
    min: metersFromFeet(150),
    max: metersFromFeet(3300),
    step: metersFromFeet(25),
    def: metersFromFeet(800),
    presets: [metersFromFeet(300), metersFromFeet(800), metersFromFeet(1300), metersFromFeet(2000)],
    label: 'Shelter radius',
  },
  exclusion: {
    min: metersFromFeet(50),
    max: metersFromFeet(1000),
    step: metersFromFeet(25),
    def: metersFromFeet(300),
    presets: [metersFromFeet(150), metersFromFeet(250), metersFromFeet(300), metersFromFeet(500)],
    label: 'Release radius',
  },
  pipeburst: {
    min: metersFromFeet(50),
    max: metersFromFeet(1300),
    step: metersFromFeet(25),
    def: metersFromFeet(175),
    presets: [metersFromFeet(175), metersFromFeet(325), metersFromFeet(650)],
    label: 'Isolation Distance',
  },
  rupture: {
    min: metersFromFeet(50),
    max: metersFromFeet(1300),
    step: metersFromFeet(25),
    def: metersFromFeet(175),
    presets: [metersFromFeet(175), metersFromFeet(325), metersFromFeet(650)],
    label: 'Isolation Distance',
  },
  tornado: {
    min: metersFromFeet(200),
    // Stored as radius. The slider shows width (twice this). Max width is 8 mi.
    // The widest tornado on record was ~2.6 mi across, the 1.3 mi radius preset.
    // No wider chip: nothing that wide has occurred.
    max: metersFromMiles(4),
    step: metersFromFeet(50),
    // New tornados start on the first width chip (1600 ft across).
    def: metersFromFeet(800),
    presets: [
      metersFromFeet(800),
      metersFromFeet(1600),
      metersFromFeet(5280),
      metersFromMiles(1.3),
    ],
    label: 'Width',
  },
  hurricane: {
    // Stored as radius. The slider and chips show width (twice this), in miles.
    // Category does not set this. A compact core is 25 mi across; a large
    // hurricane-force field is nearer 100. Tropical-storm-force wind often
    // extends farther than the slider.
    min: metersFromMiles(1),
    max: metersFromMiles(60),
    step: metersFromMiles(0.5),
    def: metersFromMiles(12.5),
    presets: [
      metersFromMiles(5),
      metersFromMiles(12.5),
      metersFromMiles(25),
      metersFromMiles(50),
    ],
    label: 'Width',
    widthInMiles: true,
  },
}

/** S / M / L maps to a radius in meters for zone hazards that use icon size as a radius shortcut.
 * Plume sources (gas leak / vapor) omit Size — footprint is the separate slider. */
export const SIZE_RADIUS_M: Partial<Record<string, Record<'sm' | 'md' | 'lg', number>>> = {
  fire: { sm: metersFromFeet(125), md: metersFromFeet(300), lg: metersFromFeet(525) },
  explosion: { sm: metersFromFeet(175), md: metersFromFeet(325), lg: metersFromFeet(650) },
  hot_zone: { sm: metersFromFeet(150), md: metersFromFeet(300), lg: metersFromFeet(650) },
  isolation: { sm: metersFromFeet(250), md: metersFromFeet(500), lg: metersFromFeet(1000) },
  evacuate: { sm: metersFromFeet(500), md: metersFromFeet(1000), lg: metersFromFeet(2000) },
  shelter: { sm: metersFromFeet(300), md: metersFromFeet(800), lg: metersFromFeet(1600) },
  exclusion: { sm: metersFromFeet(150), md: metersFromFeet(300), lg: metersFromFeet(500) },
  pipeburst: { sm: metersFromFeet(175), md: metersFromFeet(325), lg: metersFromFeet(650) },
  rupture: { sm: metersFromFeet(175), md: metersFromFeet(325), lg: metersFromFeet(650) },
}

export const PLUME_DIST_PRESETS_M = [
  metersFromFeet(300),
  metersFromFeet(800),
  metersFromFeet(1600),
  metersFromFeet(3300),
  metersFromFeet(6500),
]

export function zoneCfgFor(typeId: string): ZoneCfg | undefined {
  return ZONE_CFG[typeId]
}

export function zoneLabel(typeId: string): string {
  return ZONE_CFG[typeId]?.label ?? 'Isolation Distance'
}

export function defaultRadiusM(typeId: string): number | undefined {
  return ZONE_CFG[typeId]?.def
}

export function windSpeedBand(mph: number): string {
  if (mph < 8) return 'Calm'
  if (mph < 25) return 'Moderate'
  if (mph < 70) return 'Strong'
  return 'Extreme'
}

/** Spread angle caption — midpoint of the 5–60° slider. */
export function plumeSpreadBand(deg: number): 'Narrow' | 'Wide' {
  return deg < 30 ? 'Narrow' : 'Wide'
}

export function windFromPhrase(cardinal: string): string {
  const names: Record<string, string> = {
    N: 'North',
    NE: 'Northeast',
    E: 'East',
    SE: 'Southeast',
    S: 'South',
    SW: 'Southwest',
    W: 'West',
    NW: 'Northwest',
  }
  return `Wind from ${names[cardinal] ?? cardinal}`
}

export function presetLabel(meters: number, units: DisplayUnits): string {
  return formatDistanceM(meters, units)
}
